import React from 'react';
import { DailyDataResponse } from '../types';
import { useDailyMetrics } from '../hooks/useDailyMetrics';
import {
  prepareSleepStageData,
  prepareNoiseData,
  preparePostureData,
  prepareTemperatureData,
  prepareOverlayData,
} from '../utils/dailyDataProcessor';
import {
  formatSleepStage,
  formatPosture,
  calculateYAxisDomain,
  calculateTemperatureYAxisDomain,
} from '../utils/dailyChartUtils';
import { DailyMetricSelector } from './DailyMetricSelector';
import { DailyOverlayChart } from './DailyOverlayChart';
import { SingleChart } from './SingleChart';

type DailyChartComponentProps = {
  data: DailyDataResponse;
};

export const DailyChartComponent: React.FC<DailyChartComponentProps> = ({ data }) => {
  const { selectedMetrics, handleMetricToggle, hasSelectedMetrics } = useDailyMetrics();

  // 各データを準備
  const sleepStageData = prepareSleepStageData(data);
  const noiseData = prepareNoiseData(data);
  const postureData = preparePostureData(data);
  const temperatureData = prepareTemperatureData(data);

  // オーバーレイ用のデータを準備
  const overlayData = prepareOverlayData(
    sleepStageData,
    noiseData,
    postureData,
    temperatureData,
    selectedMetrics
  );

  return (
    <div className="space-y-8">
      {/* 項目選択エリア */}
      <DailyMetricSelector
        selectedMetrics={selectedMetrics}
        onMetricToggle={handleMetricToggle}
      />

      {/* オーバーレイチャート */}
      {hasSelectedMetrics && (
        <DailyOverlayChart
          overlayData={overlayData}
          sleepStageData={sleepStageData}
          noiseData={noiseData}
          temperatureData={temperatureData}
          selectedMetrics={selectedMetrics}
        />
      )}

      {/* 睡眠ステージ */}
      <SingleChart
        title="睡眠ステージ"
        data={sleepStageData}
        color="#3b82f6"
        yAxisDomain={[-0.3, 3.3]}
        yAxisTicks={[0, 1, 2, 3]}
        yAxisFormatter={formatSleepStage}
        valueFormatter={formatSleepStage}
        lineType="stepAfter"
      />

      {/* ノイズレベル */}
      <SingleChart
        title="ノイズレベル"
        data={noiseData}
        color="#ef4444"
        unit="dB"
        yAxisDomain={calculateYAxisDomain(noiseData.map(d => d.value as number), 15)}
        lineType="monotone"
      />

      {/* 姿勢 */}
      <SingleChart
        title="姿勢"
        data={postureData}
        color="#10b981"
        yAxisDomain={[-0.3, 2.3]}
        yAxisTicks={[2, 1, 0]}
        yAxisFormatter={formatPosture}
        valueFormatter={formatPosture}
        lineType="stepAfter"
        reversed={true}
      />

      {/* ベッド温度 */}
      <SingleChart
        title="ベッド温度"
        data={temperatureData}
        color="#f59e0b"
        unit="℃"
        yAxisDomain={calculateTemperatureYAxisDomain(temperatureData.map(d => d.value as number), 2)}
        lineType="monotone"
      />
    </div>
  );
};